import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAuth } from './AuthContext'
import {
  WatchlistData,
  WatchlistEntityType,
  WatchlistItem,
  WatchlistList,
  addToWatchlist,
  applyWatchlistItem,
  createWatchlistList,
  deleteWatchlistList,
  fetchWatchlist,
  findWatchlistItem,
  flattenWatchlist,
  insertWatchlistList,
  patchWatchlistItem,
  patchWatchlistListMeta,
  removeFromWatchlist,
  removeFromWatchlistLists,
  removeWatchlistItemFromData,
  removeWatchlistListFromData,
  renameWatchlistList,
  saveWatchlistListTree,
  setWatchlistItemLists,
  updateWatchlistItemNotificationPreferences,
} from '../services/watchlist'
import type { NotificationPreferenceUpdate } from '../types/notifications'
import { moveList as moveListInTree, type ListDropPosition } from '../utils/watchlistTree'

interface WatchlistContextValue {
  data: WatchlistData
  lists: WatchlistList[]
  loading: boolean
  isWatched: (entityType: WatchlistEntityType, entityId: string) => boolean
  findItem: (
    entityType: WatchlistEntityType,
    entityId: string
  ) => WatchlistItem | undefined
  refresh: () => Promise<void>
  addItem: (
    entityType: WatchlistEntityType,
    entityId: string,
    displayName: string,
    listIds?: number[],
    countryCode?: string | null
  ) => Promise<void>
  setItemLists: (itemId: number, listIds: number[]) => Promise<void>
  removeFromLists: (itemId: number, listIds: number[]) => Promise<void>
  removeItem: (itemId: number) => Promise<void>
  createList: (name: string) => Promise<WatchlistList>
  renameList: (listId: number, name: string) => Promise<void>
  deleteList: (listId: number) => Promise<void>
  /**
   * Move a list (and its sublists) before, into or after another list;
   * null target + 'after' = end of the top level. Shown at once, saved in
   * the background, put back if saving fails. False when not allowed.
   */
  moveList: (
    listId: number,
    targetId: number | null,
    position: ListDropPosition
  ) => Promise<boolean>
  updateItemNotificationPreferences: (
    itemId: number,
    preferences: NotificationPreferenceUpdate[]
  ) => Promise<WatchlistItem>
}

const emptyData: WatchlistData = { lists: [], uncategorized: [] }

const WatchlistContext = createContext<WatchlistContextValue | undefined>(undefined)

export const WatchlistProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth()
  const [data, setData] = useState<WatchlistData>(emptyData)
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!user) {
      setData(emptyData)
      return
    }
    setLoading(true)
    try {
      setData(await fetchWatchlist())
    } catch {
      setData(emptyData)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    refresh()
  }, [refresh])

  const flatItems = useMemo(() => flattenWatchlist(data), [data])

  const isWatched = useCallback(
    (entityType: WatchlistEntityType, entityId: string) =>
      flatItems.some(
        (i) => i.entityType === entityType && i.entityId === String(entityId)
      ),
    [flatItems]
  )

  const findItem = useCallback(
    (entityType: WatchlistEntityType, entityId: string) =>
      findWatchlistItem(data, entityType, String(entityId)),
    [data]
  )

  const addItem = useCallback(
    async (
      entityType: WatchlistEntityType,
      entityId: string,
      displayName: string,
      listIds: number[] = [],
      countryCode?: string | null
    ) => {
      const item = await addToWatchlist(
        entityType,
        String(entityId),
        displayName,
        listIds,
        countryCode
      )
      setData((prev) => applyWatchlistItem(prev, item))
    },
    []
  )

  const setItemListsFn = useCallback(async (itemId: number, listIds: number[]) => {
    const item = await setWatchlistItemLists(itemId, listIds)
    setData((prev) => applyWatchlistItem(prev, item))
  }, [])

  const removeFromListsFn = useCallback(
    async (itemId: number, listIds: number[]) => {
      await removeFromWatchlistLists(itemId, listIds)
      setData((prev) => {
        const item = flattenWatchlist(prev).find((entry) => entry.id === itemId)
        if (!item) return prev
        const nextListIds = item.listIds.filter((id) => !listIds.includes(id))
        if (nextListIds.length === 0) {
          return removeWatchlistItemFromData(prev, itemId)
        }
        return applyWatchlistItem(prev, { ...item, listIds: nextListIds })
      })
    },
    []
  )

  const removeItem = useCallback(async (itemId: number) => {
    await removeFromWatchlist(itemId)
    setData((prev) => removeWatchlistItemFromData(prev, itemId))
  }, [])

  const createList = useCallback(async (name: string) => {
    const list = await createWatchlistList(name)
    setData((prev) => insertWatchlistList(prev, list))
    return list
  }, [])

  const renameList = useCallback(async (listId: number, name: string) => {
    const list = await renameWatchlistList(listId, name)
    setData((prev) => patchWatchlistListMeta(prev, list))
  }, [])

  const deleteList = useCallback(async (listId: number) => {
    await deleteWatchlistList(listId)
    setData((prev) => removeWatchlistListFromData(prev, listId))
  }, [])

  const moveList = useCallback(
    async (listId: number, targetId: number | null, position: ListDropPosition) => {
      const before = data.lists
      const next = moveListInTree(before, listId, targetId, position)
      if (!next) return false
      if (next === before) return true
      setData((prev) => ({ ...prev, lists: next }))
      try {
        const saved = await saveWatchlistListTree(
          next.map((l) => ({ id: l.id, parentId: l.parentId ?? null }))
        )
        const meta = new Map(saved.map((l) => [l.id, l]))
        setData((prev) => ({
          ...prev,
          lists: prev.lists.map((l) => {
            const m = meta.get(l.id)
            return m ? { ...l, parentId: m.parentId ?? null, sortOrder: m.sortOrder } : l
          }),
        }))
      } catch (err) {
        setData((prev) => ({
          ...prev,
          lists: before.map((b) => {
            const current = prev.lists.find((l) => l.id === b.id)
            return current ? { ...current, parentId: b.parentId ?? null, sortOrder: b.sortOrder } : b
          }),
        }))
        throw err
      }
      return true
    },
    [data.lists]
  )

  const updateItemNotificationPreferencesFn = useCallback(
    async (itemId: number, preferences: NotificationPreferenceUpdate[]) => {
      const item = await updateWatchlistItemNotificationPreferences(itemId, preferences)
      setData((prev) => patchWatchlistItem(prev, item))
      return item
    },
    []
  )

  const value = useMemo(
    () => ({
      data,
      lists: data.lists,
      loading,
      isWatched,
      findItem,
      refresh,
      addItem,
      setItemLists: setItemListsFn,
      removeFromLists: removeFromListsFn,
      removeItem,
      createList,
      renameList,
      deleteList,
      moveList,
      updateItemNotificationPreferences: updateItemNotificationPreferencesFn,
    }),
    [
      data,
      loading,
      isWatched,
      findItem,
      refresh,
      addItem,
      setItemListsFn,
      removeFromListsFn,
      removeItem,
      createList,
      renameList,
      deleteList,
      moveList,
      updateItemNotificationPreferencesFn,
    ]
  )

  return (
    <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>
  )
}

export function useWatchlist(): WatchlistContextValue {
  const ctx = useContext(WatchlistContext)
  if (!ctx) {
    throw new Error('useWatchlist must be used inside <WatchlistProvider>')
  }
  return ctx
}
